import apiClient from '../client';
import { ApiUrls } from '../urls';
import type { Project, ProjectFormData, ProjectMetaData, SubDomainData } from '../../types/project.types';
import type { BulkUpdateProjectItem, BulkUpdateProjectsResponse } from '../../types/subscription.types';

class ProjectService {
    /**
     * Create a new project
     */
    async createProject(projectData: { name: string; tmp_conversation_id?: string }): Promise<Project> {
        try {
            const response = await apiClient.post<Project>(ApiUrls.createProject, projectData);
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Create a sample project
     */
    async createSampleProject(): Promise<Project> {
        try {
            const response = await apiClient.post<Project>(ApiUrls.createSampleProject);
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get a project by ID
     */
    async getProject(projectId: string): Promise<Project> {
        try {
            const response = await apiClient.get<Project>(ApiUrls.getProject(projectId));
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get user projects with pagination
     */
    async getUserProjects(limit: number = 50, page: number = 1): Promise<Project[]> {
        try {
            const response = await apiClient.get<Project[]>(
                ApiUrls.getUserProjectsWithPagination(limit, page)
            );
            return response.data || [];
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Update a project
     */
    async updateProject(projectId: string, projectData: ProjectFormData): Promise<Project> {
        try {
            const response = await apiClient.patch<any>(
                ApiUrls.updateProject(projectId),
                projectData
            );
            return response.data?.data || response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Delete a project
     */
    async deleteProject(projectId: string): Promise<void> {
        try {
            await apiClient.delete(ApiUrls.deleteProject(projectId));
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get project metadata (AI-generated project details)
     */
    async getProjectMetadata(params: {
        tmp_conversation_id?: string;
        user_message?: string;
    }): Promise<ProjectMetaData> {
        try {
            const response = await apiClient.post<ProjectMetaData>(
                ApiUrls.projectMetadata(),
                params
            );
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Check site availability
     */
    async checkSiteAvailability(data: SubDomainData): Promise<any> {
        try {
            const response = await apiClient.post(ApiUrls.checkSiteAvailability, data);
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get project conversations
     */
    async getProjectConversations(projectId: string): Promise<any> {
        try {
            const response = await apiClient.get(ApiUrls.projectConversations(projectId));
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Upload project files/attachments
     */
    async uploadProjectFiles(projectId: string, formData: FormData): Promise<any> {
        try {
            // Ensure we don't send the default application/json Content-Type from the client instance.
            // Passing an explicit undefined Content-Type tells the browser to set the multipart boundary.
            const response = await apiClient.post(ApiUrls.uploadProjectFiles(projectId), formData, {
                headers: { 'Content-Type': undefined as any },
            });
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get project attachments
     */
    async getProjectAttachments(projectId: string): Promise<any> {
        try {
            const response = await apiClient.get(ApiUrls.getProjectAttachments(projectId));
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Get design system for a project
     */
    async getDesignSystem(projectId: string): Promise<any> {
        try {
            const url = `${ApiUrls.getProject(projectId)}/design-system`;
            const response = await apiClient.get(url);
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Update (patch) design system for a project
     * Expects a plain design system object (no wrapper)
     */
    async updateDesignSystem(projectId: string, payload: any): Promise<any> {
        try {
            const url = `${ApiUrls.getProject(projectId)}/design-system`;
            const response = await apiClient.patch(url, payload);
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Upload a logo specifically for the design system
     * Expects multipart/form-data with field name 'logo'
     */
    async uploadDesignSystemLogo(projectId: string, formData: FormData): Promise<any> {
        try {
            const url = `${ApiUrls.getProject(projectId)}/design-system/logo`;
            // Ensure the default application/json header is not sent so the browser can set the multipart boundary.
            const response = await apiClient.post(url, formData, {
                headers: { 'Content-Type': undefined as any },
            });
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Delete the design-system logo for a project
     * Endpoint: DELETE /projects/{projectId}/design-system/logo
     */
    async deleteDesignSystemLogo(projectId: string): Promise<void> {
        try {
            const url = `${ApiUrls.getProject(projectId)}/design-system/logo`;
            await apiClient.delete(url);
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Delete project attachment
     */
    async deleteProjectAttachment(fileId: string): Promise<void> {
        try {
            await apiClient.delete(ApiUrls.deleteProjectAttachment(fileId));
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Save or update design system for a project.
     * Tries PUT first, falls back to POST on 404 (some backends expect create vs update).
     */
    async saveDesignSystem(projectId: string, payload: any): Promise<any> {
        try {
            const url = `${ApiUrls.getProject(projectId)}/design-system`;
            // Prefer PUT (idempotent update) then fallback to POST if not found
            try {
                const response = await apiClient.put(url, payload);
                return response.data;
            } catch (err: any) {
                if (err?.response?.status === 404) {
                    const response = await apiClient.post(url, payload);
                    return response.data;
                }
                throw err;
            }
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Bulk update multiple projects
     * Used for archiving projects after downgrade to Free plan
     *
     * @param updates - Array of project updates with id and data
     * @returns Response with success message and updated count
     *
     * @example
     * ```typescript
     * await projectService.bulkUpdateProjects([
     *   { id: 'proj-1', data: { isActive: false } },
     *   { id: 'proj-2', data: { isActive: false } },
     * ]);
     * ```
     */
    async bulkUpdateProjects(updates: {projects: BulkUpdateProjectItem[]}): Promise<BulkUpdateProjectsResponse> {
        try {
            const response = await apiClient.patch<BulkUpdateProjectsResponse>(
                ApiUrls.bulkUpdateProjects,
                updates
            );
            return response.data;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    /**
     * Handle API errors consistently
     */
    private handleError(error: any): Error {
        if (error.response) {
            const message = error.response.data?.message || 'Request failed';
            return new Error(message);
        }

        // Network error
        return new Error('Network error. Please check your connection.');
    }
}

export const projectService = new ProjectService();
export default projectService;

